import { InfoOutlined } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from '../../../../design';
import { useForceRerender, useRequester } from '../../../../hooks';
import { EikenMaterialData, EikenMaterialResource } from '../../../../resources';

type EikenMaterialManualFormData = Partial<EikenMaterialData>;

export type FormData = {
  id?: string;
  title: string;
  pronunciation: string;
  imageText: string;
  pronunciationQuestion: string;
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  image?: string;
};

export function EikenMaterialManualForm({
  eikenMaterial,
  formValue,
  onFormChange,
}: {
  eikenMaterial?: Partial<EikenMaterialData>;
  formValue?: FormData;
  onFormChange?: (data: FormData | undefined, id?: string) => void;
}) {
  const forceRender = useForceRerender();
  const [isLoading, setIsLoading] = useState(false);
  const [isEditTitle, setIsEditTitle] = useState(false);
  const imageRef = useRef<HTMLInputElement>(null);
  const image = imageRef.current?.files?.[0];
  const navigate = useNavigate();

  const saveMaterial = useRequester<EikenMaterialResource>({
    onSubmit: (data: any) =>
      eikenMaterial?.id
        ? EikenMaterialResource.updateOne(eikenMaterial.id, data)
        : EikenMaterialResource.createOne(data),
    onSuccess: (ob) =>
      onFormChange ? onFormChange(undefined, ob.data.id) : navigate(`/eiken-materials`),
    onError: console.log,
  });

  async function handleDeleteTest() {
    if (eikenMaterial?.id) {
      await EikenMaterialResource.deleteOne(eikenMaterial.id);
    }
    navigate(`/eiken-materials`);
  }

  async function handleSubmit(data: FormData) {
    try {
      setIsLoading(true);

      const formData: EikenMaterialManualFormData = {
        referenceTextTitle: data.title,
        createdAt: new Date().toISOString(),
        referenceText: data.pronunciationQuestion,
        imageText: data.imageText,
        content: {
          q1: data.q1,
          q2: data.q2,
          q3: data.q3,
          q4: data.q4,
          pronunciation: data.pronunciation,
        },
      };
      const image = imageRef.current?.files?.[0];
      if (image) {
        formData.image = await EikenMaterialResource.upload(image, 'image');
      }

      await saveMaterial.submit(formData);
    } finally {
      setIsLoading(false);
    }
  }

  async function uploadImage() {
    if (onFormChange && eikenMaterial) {
      const image = imageRef.current?.files?.[0];
      if (!image) return;
      const uploadedImage = await EikenMaterialResource.upload(image, 'image');
      if (uploadedImage) {
        const newFormValue: FormData = {
          ...formValue,
          image: uploadedImage,
        } as FormData;
        onFormChange(newFormValue);
      }
    } else {
      setTimeout(forceRender, 100);
    }
  }

  async function handleDownloadImage() {
    if (!eikenMaterial?.image) return;

    try {
      const imageUrl = eikenMaterial.image;
      const fileName = imageUrl.split('/').pop() || 'downloaded-image.png';

      // Fetch the image as a blob
      const response = await fetch(imageUrl);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();

      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;

      // Append to the document, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  }

  return (
    <Box sx={{ p: 2, background: '#fafbfc', minHeight: '100vh' }}>
      <Form
        onSubmit={handleSubmit}
        onChange={onFormChange}
        disabled={isLoading || saveMaterial.loading}
      >
        <Paper elevation={1} sx={{ p: 4, borderRadius: 3, maxWidth: 900, mx: 'auto' }}>
          {isEditTitle ? (
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="h4" fontWeight={600} mr={1}>
                Test title
              </Typography>
              <IconButton size="small" onClick={() => setIsEditTitle(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <>
              <Typography fontWeight={500} mb={1}>
                Title
              </Typography>
              <TextField
                fullWidth
                multiline
                name="title"
                minRows={1}
                placeholder="Enter a title"
                variant="outlined"
                defaultValue={eikenMaterial?.referenceTextTitle}
                sx={{ mb: 3 }}
              />
            </>
          )}
          <Typography fontWeight={500} mb={1}>
            Pronunciation paragraph text
          </Typography>
          <TextField
            fullWidth
            multiline
            name="pronunciationQuestion"
            minRows={2}
            placeholder="Enter a description"
            variant="outlined"
            defaultValue={eikenMaterial?.referenceText}
            sx={{
              mb: 3,
              '& .MuiInputBase-root': {
                '& textarea': {
                  resize: 'vertical',
                },
              },
            }}
          />
          <Grid container spacing={3}>
            {/* Image upload section */}
            <Grid item xs={12} md={4}>
              <Typography fontWeight={500} mb={1}>
                Image
              </Typography>
              {(eikenMaterial?.image || image) && (
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff',
                    position: 'relative',
                    p: 1,
                  }}
                >
                  <img
                    src={image ? URL.createObjectURL(image) : eikenMaterial?.image}
                    alt="Uploaded"
                    style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                  />
                </Box>
              )}
              <Box display="flex" gap={1} mb={1}>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => imageRef.current?.click()}
                >
                  Upload
                  <input
                    ref={imageRef}
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={() => uploadImage()}
                  />
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled={!eikenMaterial?.image}
                  onClick={handleDownloadImage}
                >
                  Download
                </Button>
              </Box>
              <Box display="flex" alignItems="center" color="text.secondary">
                <InfoOutlined fontSize="small" />
                <span className="text-sm ml-1">Download a sample image</span>
                <a className="text-blue-500 text-sm ml-1" href="/assets/sample.png" download>
                  here
                </a>
              </Box>
            </Grid>
            {/* Text fields section */}
            <Grid item xs={12} md={8}>
              <>
                <Typography fontWeight={500} mb={1}>
                  Image text
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  name="imageText"
                  minRows={1}
                  maxRows={1}
                  placeholder={`Enter the starting sentence`}
                  variant="outlined"
                  defaultValue={eikenMaterial?.imageText}
                  sx={{
                    mb: 3,
                    '& .MuiInputBase-root': {
                      '& textarea': {
                        resize: 'vertical',
                      },
                    },
                  }}
                />
              </>

              <>
                <Typography fontWeight={500} mb={1}>
                  Pronunciation question text
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  name="pronunciation"
                  minRows={1}
                  maxRows={1}
                  placeholder={`Enter a pronunciation question`}
                  variant="outlined"
                  defaultValue={eikenMaterial?.content?.pronunciation}
                  sx={{
                    mb: 3,
                    '& .MuiInputBase-root': {
                      '& textarea': {
                        resize: 'vertical',
                      },
                    },
                  }}
                />
              </>

              <Typography fontWeight={500} mb={1}>
                Question 1 text
              </Typography>
              <TextField
                fullWidth
                multiline
                name={`q1`}
                minRows={1}
                maxRows={1}
                placeholder={`Enter a question 1`}
                defaultValue={eikenMaterial?.content?.q1}
                variant="outlined"
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': {
                    '& textarea': {
                      resize: 'vertical',
                    },
                  },
                }}
              />
              <Typography fontWeight={500} mb={1}>
                Question 2 text
              </Typography>
              <TextField
                fullWidth
                multiline
                name={`q2`}
                minRows={1}
                maxRows={1}
                placeholder={`Enter a question 2`}
                defaultValue={eikenMaterial?.content?.q2}
                variant="outlined"
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': {
                    '& textarea': {
                      resize: 'vertical',
                    },
                  },
                }}
              />
              <Typography fontWeight={500} mb={1}>
                Question 3 text
              </Typography>
              <TextField
                fullWidth
                multiline
                name={`q3`}
                minRows={1}
                maxRows={1}
                placeholder={`Enter a question 3`}
                defaultValue={eikenMaterial?.content?.q3}
                variant="outlined"
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': {
                    '& textarea': {
                      resize: 'vertical',
                    },
                  },
                }}
              />
              <Typography fontWeight={500} mb={1}>
                Question 4 text
              </Typography>
              <TextField
                fullWidth
                multiline
                name={`q4`}
                minRows={1}
                maxRows={1}
                placeholder={`Enter a question 4`}
                defaultValue={eikenMaterial?.content?.q4}
                variant="outlined"
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': {
                    '& textarea': {
                      resize: 'vertical',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              variant="outlined"
              onClick={handleDeleteTest}
              color="error"
              disabled={!eikenMaterial?.id}
            >
              Delete test
            </Button>
            <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 180 }}>
              Save question
            </Button>
          </Box>
        </Paper>
      </Form>
    </Box>
  );
}
